// MATERIAL UI IMPORTS
import { FormControl } from "@mui/material";

// C&B COMPONENTS
import NumberFormatCustom from "src/components/trade/NumberField";

// STYLES
import {
  StyledFormLabel,
  StyledInputContainer,
} from "../wallets/common/styles/CryptoWithdrawalStyled";
import { StyledError } from "../wallets/Modal/Withdraw/Error";

export enum InputType {
  TEXT = "text",
  NUMBER = "number",
  ERROR = "error",
}

export enum InputStyleType {
  DEFAULT = "DEFAULT",
  FORM = "FORM",
  ERROR = "ERROR",
  FORM_ERROR = "FORM_ERROR",
}

interface IFormInput {
  id?: string;
  title?: string;
  type?: string;
  inputType?: InputType;
  value?: string | number | boolean;
  name?: string;
  onChange?: (value: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (value: React.FocusEvent<HTMLInputElement>) => void;
  variant?: "outlined" | undefined;
  disabled?: boolean;
  InputAdornment?: React.ReactNode;
  styleType?: InputStyleType;
  placeholder?: string;
  label?: string;
  shrinkLabel?: boolean;
  sx?: Record<string, any>;
  inputSx?: Record<string, any>;
  /** Props forwarded to the underlying input element (or to NumericFormat when inputType is NUMBER). */
  inputProps?: Record<string, any>;
  errorMessage?: string;
}

/**
 * Created this function to prevent user from entering 'e', 'E', '+', '-' in the number input field.
 *
 * @param {React.KeyboardEvent<HTMLDivElement>} e - The keyboard event.
 */
const handleNumberKeyDown = (e: any, type: string) => {
  const skipCharacters = ["e", "E", "+", "-"];
  if (type === "number") {
    if (skipCharacters.includes(e.key)) {
      e.preventDefault();
    }
  }
};

/**
 * A Form Input component that can be used to render a text input field.
 *
 * @function
 * @param {string} id - The id of the input field.
 * @param {string} title - The title to display as the label for the input field.
 * @param {string} inputType - The input type of the input component.
 * @param {string} type - The type of input component.
 * @param {string} value - The value of the input field.
 * @param {string} name - The name of the input field. This is required when dynamically setting inputs and keeping previous states.
 * @param {function} onChange - The function to call when the input field changes.
 * @param {function} onBlur - The function to call when the input field loses focus.
 * @param {string} variant - The variant of the input field.
 * @param {boolean} disabled - Whether the input field is disabled.
 * @param {React.ReactNode} InputAdornment - The input adornment to display as part of the input field.
 * @param {InputStyleType} styleType - The style type of the input field.
 * @param {string} placeholder - The placeholder of the input field.
 * @param {string} label - An optional label for the input field.
 * @param {boolean} shrinkLabel - Adjusts the behaviour of lables in the input box. If false, the label will act as a placeholder until the input is clicked.
 * @param {Record<string, any>} sx - The custom styles to be applied to the component.
 * @param {Record<string, any>} inputSx - Custom styles to be applied to the input of the component.
 * @param {Record<string, any>} inputProps - Props forwarded to the underlying input element. When inputType is NUMBER these reach NumericFormat, so react-number-format options (decimalScale, prefix, ...) can be set per field.
 * @param {string} errorMessage - Optional error message to display below the input.
 * @returns {JSX.Element} The JSX code to render the Form Input component.
 */
export const FormInput = ({
  id,
  title,
  type = "text",
  inputType = InputType.TEXT,
  value,
  name,
  onChange,
  onBlur,
  variant = "outlined",
  disabled = false,
  InputAdornment,
  styleType = InputStyleType.DEFAULT,
  placeholder,
  label,
  shrinkLabel = true,
  sx,
  inputSx,
  inputProps,
  errorMessage,
}: IFormInput): JSX.Element => {
  return (
    <FormControl
      style={{
        width: "100%",
        position: "relative",
      }}>
      {title && <StyledFormLabel>{title}</StyledFormLabel>}
      <StyledInputContainer
        id={id}
        value={value}
        type={type}
        name={name}
        variant={variant}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        onKeyDown={(e) => handleNumberKeyDown(e, type)}
        InputLabelProps={{
          shrink: shrinkLabel,
        }}
        InputProps={{
          inputComponent: inputType === InputType.NUMBER ? (NumberFormatCustom as any) : undefined,
          endAdornment: InputAdornment,
          inputProps,
          sx: inputSx,
        }}
        label={label}
        disabled={disabled}
        styletype={styleType}
        sx={sx}
      />
      {errorMessage && <StyledError>{errorMessage}</StyledError>}
    </FormControl>
  );
};
